import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Theme, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { addDays, format, isBefore, isEqual } from 'date-fns';
import { BooleanField, BooleanInput, Create, Datagrid, DateInput, Edit, EditButton, FormTab, List, NumberField, NumberInput, SelectInput, Show, ShowActions, SimpleList, SimpleShowLayout, TabbedForm, TextField, TextInput, required, usePermissions, useResourceDefinition } from 'react-admin';
import { useParams } from 'react-router-dom';
import Activities from 'utils/GeneratedModels/Activities';
import ApplicationFormReferenceField from 'utils/GeneratedRaReference/ApplicationFormReferenceField';
import ApplicationFormReferenceInput from 'utils/GeneratedRaReference/ApplicationFormReferenceInput';
import OptionReferenceField from 'utils/GeneratedRaReference/OptionReferenceField';
import OptionReferenceInput from 'utils/GeneratedRaReference/OptionReferenceInput';
import DateFnsField from 'utils/react-admin/DateFnsField';
import GridWrapper from 'utils/react-admin/GridWrapper';
import XlsHtmlExporter from 'utils/react-admin/exporters/XlsHtml';
import HtmlField from 'utils/react-admin/fields/HtmlField';
import DateRangeFilterInput from 'utils/react-admin/filters/DateRangeFilterInput';
import FileManagerModalInput from 'utils/react-admin/inputs/FileManagerModalInput';
import RichTextInputEx from 'utils/react-admin/inputs/RichTextInputEx';
import TinyMCEInput from 'utils/react-admin/inputs/TinyMCEInput';
import { Permissions } from '../../ra-php-crud-api-auth';
import ArrayForm from 'utils/react-admin/inputs/BlocksInput/ArrayForm';
import BoundedInput from 'utils/react-admin/inputs/BlocksInput/BoundedInput';

export const CHOICES_ACTIVITIES_DIRECT_LINK_TYPE = [
  { id: 'internal', name: 'internal', },
  { id: 'external', name: 'external', },
];
// const RESOURCE_FIELDS = {}
const ResourceFilters = [
  <TextInput source="search" alwaysOn />,
  <BooleanInput source="is_public" fullWidth />,
  <BooleanInput source="is_highlight" fullWidth />,
  <BooleanInput source="is_payment" fullWidth />,
  <NumberInput source="position" fullWidth />,
  <DateInput source="start_date" fullWidth />,
  <DateInput source="end_date" fullWidth />,
  <BooleanInput source="display_on_mon" fullWidth />,
  <BooleanInput source="display_on_tue" fullWidth />,
  <BooleanInput source="display_on_wed" fullWidth />,
  <BooleanInput source="display_on_thu" fullWidth />,
  <BooleanInput source="display_on_fri" fullWidth />,
  <BooleanInput source="display_on_sat" fullWidth />,
  <BooleanInput source="display_on_sun" fullWidth />,
  <TextInput source="direct_link_en" fullWidth />,
  <TextInput source="direct_link_tc" fullWidth />,
  <TextInput source="direct_link_sc" fullWidth />,
  <SelectInput source="direct_link_type" fullWidth choices={CHOICES_ACTIVITIES_DIRECT_LINK_TYPE} />,
  <TextInput source="title_en" fullWidth />,
  <TextInput source="title_tc" fullWidth />,
  <TextInput source="title_sc" fullWidth />,
  <TextInput source="short_title_en" fullWidth />,
  <TextInput source="short_title_tc" fullWidth />,
  <TextInput source="short_title_sc" fullWidth />,
  <OptionReferenceInput source="activity_category_id" fullWidth filter={{ option_type: 'activity_category' }} />,
  <TextField source="short_description_en_html" fullWidth />,
  <TextField source="short_description_tc_html" fullWidth />,
  <TextField source="short_description_sc_html" fullWidth />,
  <TextField source="description_en_html" fullWidth />,
  <TextField source="description_tc_html" fullWidth />,
  <TextField source="description_sc_html" fullWidth />,
  <BooleanInput source="show_application_button" fullWidth />,
  <ApplicationFormReferenceInput source="application_form_id" fullWidth />,
  <DateRangeFilterInput source="created" fullWidth />,
  <TextInput source="createdBy" fullWidth />,
  <DateRangeFilterInput source="updated" fullWidth />,
  <TextInput source="updatedBy" fullWidth />,
];

const validateIsPublic: any[] = [required()];
const validateIsHighlight: any[] = [];
const validateIsPayment: any[] = [];
const validatePosition: any[] = [];
const validateStartDate: any[] = [];
const validateEndDate: any[] = [];
const validateDisplayOnMon: any[] = [];
const validateDisplayOnTue: any[] = [];
const validateDisplayOnWed: any[] = [];
const validateDisplayOnThu: any[] = [];
const validateDisplayOnFri: any[] = [];
const validateDisplayOnSat: any[] = [];
const validateDisplayOnSun: any[] = [];
const validateDirectLinkEn: any[] = [];
const validateDirectLinkTc: any[] = [];
const validateDirectLinkSc: any[] = [];
const validateDirectLinkType: any[] = [];
const validateTitleEn: any[] = [];
const validateTitleTc: any[] = [];
const validateTitleSc: any[] = [];
const validateShortTitleEn: any[] = [];
const validateShortTitleTc: any[] = [];
const validateShortTitleSc: any[] = [];
const validateActivityCategoryId: any[] = [];
// const validateImageFm: any[] = [];
// const validateShortDescriptionEnHtml: any[] = [];
// const validateShortDescriptionTcHtml: any[] = [];
// const validateShortDescriptionScHtml: any[] = [];
// const validateDescriptionEnHtml: any[] = [];
// const validateDescriptionTcHtml: any[] = [];
// const validateDescriptionScHtml: any[] = [];
const validateShowApplicationButton: any[] = [];
const validateApplicationFormId: any[] = [];

export const ResourceList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <List filters={ResourceFilters} sort={{ field: 'created', order: 'DESC' }} exporter={XlsHtmlExporter}>
      {isSmall ? (
        <SimpleList linkType="edit"
          primaryText={(record: any) => `${record.title_tc} (#${record.id})`}
          secondaryText={record => `${record.start_date} ~ ${record.end_date}`}
        ></SimpleList>)
        : (
          <Datagrid>
            <TextField source="id" />
            <BooleanField source="is_public" fullWidth />
            <BooleanField source="is_highlight" fullWidth />
            <BooleanField source="is_payment" fullWidth />
            <NumberField source="position" fullWidth />
            <DateFnsField source="start_date" fullWidth />
            <DateFnsField source="end_date" fullWidth />
            {/* <BooleanField source="display_on_mon" fullWidth /> */}
            {/* <BooleanField source="display_on_tue" fullWidth /> */}
            {/* <BooleanField source="display_on_wed" fullWidth /> */}
            {/* <BooleanField source="display_on_thu" fullWidth /> */}
            {/* <BooleanField source="display_on_fri" fullWidth /> */}
            {/* <BooleanField source="display_on_sat" fullWidth /> */}
            {/* <BooleanField source="display_on_sun" fullWidth /> */}
            {/* <TextField source="direct_link_type" fullWidth /> */}
            <TextField source="title_en" fullWidth />
            <TextField source="title_tc" fullWidth />
            <TextField source="title_sc" fullWidth />
            {/* <TextField source="short_title_en" fullWidth /> */}
            {/* <TextField source="short_title_tc" fullWidth /> */}
            {/* <TextField source="short_title_sc" fullWidth /> */}
            <OptionReferenceField source="activity_category_id" fullWidth />
            {/* <HtmlField source="short_description_en_html" fullWidth showButton /> */}
            {/* <HtmlField source="short_description_tc_html" fullWidth showButton /> */}
            {/* <HtmlField source="short_description_sc_html" fullWidth showButton /> */}
            {/* <HtmlField source="description_en_html" fullWidth showButton /> */}
            {/* <HtmlField source="description_tc_html" fullWidth showButton /> */}
            {/* <HtmlField source="description_sc_html" fullWidth showButton /> */}
            {/* <BooleanField source="show_application_button" fullWidth /> */}
            {/* <ApplicationFormReferenceField source="application_form_id" fullWidth /> */}
            <DateFnsField source="created" />
            <DateFnsField source="updated" />
            {/* <ShowButton /> */}
            <EditButton />
          </Datagrid>)
      }
    </List>
  )
};

export const ResourceShow = () => {
  const { isLoading, permissions } = usePermissions<Permissions>();
  const { hasEdit } = useResourceDefinition();

  if (isLoading || undefined === permissions) { return (<>Loading</>); }

  return (
    <Show actions={hasEdit ? <ShowActions /> : <></>}>
      <SimpleShowLayout>
        <TextField source="id" />
        <BooleanField source="is_public" fullWidth />
        <BooleanField source="is_highlight" fullWidth />
        <BooleanField source="is_payment" fullWidth />
        <NumberField source="position" fullWidth />
        <DateFnsField source="start_date" fullWidth />
        <DateFnsField source="end_date" fullWidth />
        <BooleanField source="display_on_mon" fullWidth />
        <BooleanField source="display_on_tue" fullWidth />
        <BooleanField source="display_on_wed" fullWidth />
        <BooleanField source="display_on_thu" fullWidth />
        <BooleanField source="display_on_fri" fullWidth />
        <BooleanField source="display_on_sat" fullWidth />
        <BooleanField source="display_on_sun" fullWidth />
        <TextField source="direct_link_en" fullWidth />
        <TextField source="direct_link_tc" fullWidth />
        <TextField source="direct_link_sc" fullWidth />
        <TextField source="direct_link_type" fullWidth />
        <TextField source="title_en" fullWidth />
        <TextField source="title_tc" fullWidth />
        <TextField source="title_sc" fullWidth />
        <TextField source="short_title_en" fullWidth />
        <TextField source="short_title_tc" fullWidth />
        <TextField source="short_title_sc" fullWidth />
        <OptionReferenceField source="activity_category_id" fullWidth />
        <HtmlField source="short_description_en_html" fullWidth />
        <HtmlField source="short_description_tc_html" fullWidth />
        <HtmlField source="short_description_sc_html" fullWidth />
        <HtmlField source="description_en_html" fullWidth />
        <HtmlField source="description_tc_html" fullWidth />
        <HtmlField source="description_sc_html" fullWidth />
        <BooleanField source="show_application_button" fullWidth />
        <ApplicationFormReferenceField source="application_form_id" fullWidth />
        <DateFnsField source="created" />
        <DateFnsField source="updated" />
      </SimpleShowLayout>
    </Show>
  )
};

const DayNameMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const make = (data: Activities) => {
  if (null !== data.start_date && null !== data.end_date) {
    if (DayNameMap.some(d => (data as any)[`display_on_${d}`])) {
      let dates = [];
      let start = new Date(data.start_date), end = new Date(data.end_date);
      for (let loopDate = start; isBefore(loopDate, end) || isEqual(loopDate, end); loopDate = addDays(loopDate, 1)) {
        if ((data as any)[`display_on_${DayNameMap[loopDate.getDay()]}`]) {
          dates.push(format(loopDate, "yyyy-MM-dd"));
        }
      }
      return JSON.stringify(dates);
    }
  }
  return null;
}

const transform = (data: Activities) => {
  console.log('transform', data);

  (data as any).__dates = make(data);
  data.image_fm = ('string' === typeof data.image_fm) ? data.image_fm : (null === data.image_fm ? null : JSON.stringify(data.image_fm));
  data.image_1_fm = ('string' === typeof data.image_1_fm) ? data.image_1_fm : (null === data.image_1_fm ? null : JSON.stringify(data.image_1_fm));
  data.image_2_fm = ('string' === typeof data.image_2_fm) ? data.image_2_fm : (null === data.image_2_fm ? null : JSON.stringify(data.image_2_fm));
  data.exception_dates = ('string' === typeof data.exception_dates) ? data.exception_dates : (null === data.exception_dates ? null : JSON.stringify(data.exception_dates));

  return data;
};

export const ResourceForm = ({ isEdit }: { isEdit?: boolean }) => {
  // const { getMany } = useDataProvider();
  const { ref, ref_id, ref_default_values } = useParams();
  // const redirect = ref_id ? `/${ref}/${ref_id}` : undefined;
  const defaultValueRefId = ref_id ? { [`${ref}_id`]: ref_id } : {};
  const refDefaultValues = ref_default_values ? JSON.parse(ref_default_values as string) : {};

  console.log(format, make({
    start_date: '2023-06-19',
    end_date: '2023-07-24',
    display_on_mon: true,
  }));
  return (
    <TabbedForm
      defaultValues={{ ...defaultValueRefId, ...refDefaultValues }}
    >
      {/* redirect={redirect} */}
      <FormTab label="ra.page.general">
        <GridWrapper>
          <Grid item xs={3}><BooleanInput source="is_public" fullWidth validate={validateIsPublic} /></Grid>
          <Grid item xs={3}><BooleanInput source="is_highlight" fullWidth validate={validateIsHighlight} /></Grid>
          <Grid item xs={3}><BooleanInput source="is_payment" fullWidth validate={validateIsPayment} /></Grid>
          <Grid item xs={3}><NumberInput source="position" fullWidth validate={validatePosition} /></Grid>
          <Grid item xs={6}><DateInput source="start_date" fullWidth validate={validateStartDate} /></Grid>
          <Grid item xs={6}><DateInput source="end_date" fullWidth validate={validateEndDate} /></Grid>
          <Grid item xs={12}>
            <div style={{ display: 'flex', }}>
              <BooleanInput source="display_on_mon" fullWidth validate={validateDisplayOnMon} />
              <BooleanInput source="display_on_tue" fullWidth validate={validateDisplayOnTue} />
              <BooleanInput source="display_on_wed" fullWidth validate={validateDisplayOnWed} />
              <BooleanInput source="display_on_thu" fullWidth validate={validateDisplayOnThu} />
              <BooleanInput source="display_on_fri" fullWidth validate={validateDisplayOnFri} />
              <BooleanInput source="display_on_sat" fullWidth validate={validateDisplayOnSat} />
              <BooleanInput source="display_on_sun" fullWidth validate={validateDisplayOnSun} />
            </div>
          </Grid>
          <Grid item xs={12}>
            <ArrayForm source="exception_dates">
              <BoundedInput source={`date`} input={<DateInput source="date" />} />
            </ArrayForm>
          </Grid>
          <Grid item xs={3}><TextInput source="direct_link_en" fullWidth validate={validateDirectLinkEn} /></Grid>
          <Grid item xs={3}><TextInput source="direct_link_tc" fullWidth validate={validateDirectLinkTc} /></Grid>
          <Grid item xs={3}><TextInput source="direct_link_sc" fullWidth validate={validateDirectLinkSc} /></Grid>
          <Grid item xs={3}><SelectInput source="direct_link_type" fullWidth choices={CHOICES_ACTIVITIES_DIRECT_LINK_TYPE} validate={validateDirectLinkType} /></Grid>
          <Grid item xs={4}><TextInput source="title_en" fullWidth validate={validateTitleEn} /></Grid>
          <Grid item xs={4}><TextInput source="title_tc" fullWidth validate={validateTitleTc} /></Grid>
          <Grid item xs={4}><TextInput source="title_sc" fullWidth validate={validateTitleSc} /></Grid>
          <Grid item xs={4}><RichTextInputEx source="short_title_en" fullWidth validate={validateShortTitleEn} /></Grid>
          <Grid item xs={4}><RichTextInputEx source="short_title_tc" fullWidth validate={validateShortTitleTc} /></Grid>
          <Grid item xs={4}><RichTextInputEx source="short_title_sc" fullWidth validate={validateShortTitleSc} /></Grid>
          <Grid item xs={12}><OptionReferenceInput source="activity_category_id" fullWidth filter={{ option_type: 'activity_category' }} validate={validateActivityCategoryId} /></Grid>
          <Grid item xs={4}><FileManagerModalInput source="image_fm" mode="image" /></Grid>
          <Grid item xs={4}><FileManagerModalInput source="image_1_fm" mode="image" /></Grid>
          <Grid item xs={4}><FileManagerModalInput source="image_2_fm" mode="image" /></Grid>
          <Grid item xs={12}><TinyMCEInput source="short_description_en_html" /></Grid>
          <Grid item xs={12}><TinyMCEInput source="short_description_tc_html" /></Grid>
          <Grid item xs={12}><TinyMCEInput source="short_description_sc_html" /></Grid>
          <Grid item xs={12}><TinyMCEInput source="description_en_html" /></Grid>
          <Grid item xs={12}><TinyMCEInput source="description_tc_html" /></Grid>
          <Grid item xs={12}><TinyMCEInput source="description_sc_html" /></Grid>
          <Grid item xs={6}><BooleanInput source="show_application_button" fullWidth validate={validateShowApplicationButton} /></Grid>
          <Grid item xs={6}><ApplicationFormReferenceInput source="application_form_id" fullWidth validate={validateApplicationFormId} /></Grid>
        </GridWrapper>
      </FormTab>

    </TabbedForm>
  )
}

export const ResourceCreate = () => {
  return (
    <Create mutationMode="pessimistic" transform={transform}>
      <ResourceForm />
    </Create>
  )
};

export const ResourceEdit = () => {
  return (
    <Edit mutationMode="pessimistic" transform={transform}>
      <ResourceForm isEdit={true} />
    </Edit>
  )
};

const Resource = {
  list: ResourceList,
  edit: ResourceEdit,
  create: ResourceCreate,
  // show: ResourceShow,
  icon: CalendarMonthIcon,
}

export default Resource;
